import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from '@tanium/react-modal';
import { Stack } from '@tanium/react-stack';
import { Form, Formik } from 'formik';
import { Button } from '@tanium/react-button';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { K8sRoleTargetAccess } from 'gql/K8sRoleTargetAccess';
import Helper from 'utils/Helper';
import FormTextField from 'components/common/form/formTextField';
import APIAccessType, { apiAccessToLocalizationKey } from 'common/IAPIAccessType';
import FormDropDown from '../form/formDropDown';

type K8sRoleTargetAccessForm = {
  adGroups: string;
  roleName: string;
  namespace: string;
  description: string;
  apiAccessType: APIAccessType;
};

const defaultCreateForm: K8sRoleTargetAccessForm = {
  adGroups: '',
  roleName: '',
  namespace: '',
  description: '',
  apiAccessType: APIAccessType.ROLE,
};

function mapK8sRoleTargetAccessToForm({
  adGroups,
  roleName,
  namespace,
  description,
  apiAccessType,
}: K8sRoleTargetAccess): K8sRoleTargetAccessForm {
  return {
    adGroups: adGroups.join(','),
    roleName,
    namespace,
    description: description || '',
    apiAccessType,
  };
}

export type Operation =
  | {
      type: 'create';
      adGroups: string[];
      roleName: string;
      namespace: string;
      description: string;
      targetOrTargetGroupId: string;
      apiAccessType: APIAccessType;
    }
  | {
      type: 'update';
      adGroups: string[];
      roleName: string;
      namespace: string;
      description: string;
      id: string;
      targetOrTargetGroupId: string;
      apiAccessType: APIAccessType;
    }
  | {
      type: 'cancel';
    };

type K8sRoleTargetAccessCRUDModalProps = {
  isOpen: boolean;
  onProcess: (operation: Operation) => void;
  k8sRoleTargetAccess?: K8sRoleTargetAccess;
  targetOrTargetGroupId: string;
};

const K8sRoleTargetAccessCRUDModal: React.VFC<K8sRoleTargetAccessCRUDModalProps> = ({
  isOpen,
  onProcess,
  k8sRoleTargetAccess,
  targetOrTargetGroupId,
}) => {
  const access =
    k8sRoleTargetAccess !== undefined ? mapK8sRoleTargetAccessToForm(k8sRoleTargetAccess) : defaultCreateForm;
  const { t } = useTranslation();
  const [apiAccessType, setApiAccessType] = useState(access.apiAccessType);

  const handleSubmit = useCallback(
    (data: K8sRoleTargetAccessForm) => {
      const cleanNamespace = data.apiAccessType === APIAccessType.CLUSTER_ROLE ? '' : data.namespace;
      const operation: Operation =
        k8sRoleTargetAccess === undefined
          ? {
              type: 'create',
              adGroups: Helper.safeConvertOptionalStringToArray(data.adGroups),
              roleName: data.roleName,
              namespace: cleanNamespace,
              description: data.description,
              targetOrTargetGroupId,
              apiAccessType: data.apiAccessType,
            }
          : {
              type: 'update',
              adGroups: Helper.safeConvertOptionalStringToArray(data.adGroups),
              roleName: data.roleName,
              namespace: cleanNamespace,
              description: data.description,
              id: k8sRoleTargetAccess.id,
              targetOrTargetGroupId: k8sRoleTargetAccess.targetOrTargetGroupID,
              apiAccessType: data.apiAccessType,
            };
      onProcess(operation);
    },
    [onProcess, k8sRoleTargetAccess, targetOrTargetGroupId]
  );

  const handleCancel = useCallback(() => {
    onProcess({
      type: 'cancel',
    });
  }, [onProcess]);

  // Use this to force the dropdown to reset on `isOpen` changing
  useEffect(() => {
    setApiAccessType(access.apiAccessType);
  }, [isOpen, setApiAccessType, access.apiAccessType]);

  const validationSchema = yup.object().shape({
    roleName: yup.string().label(t('K8S_ROLE_TARGET_ACCESS_ROLE_NAME_LABEL')).required(),
    description: yup.string().label(t('DESCRIPTION')).nullable(),
    adGroups: yup.string().label(t('AD_GROUPS')).required(),
    apiAccessType: yup.string().label(t('K8S_ROLE_TARGET_ACCESS_API_ACCESS_LABEL')).required(),
    namespace: yup.string().label(t('K8S_ROLE_TARGET_ACCESS_NAMESPACE_LABEL')).when('apiAccessType', {
      is: APIAccessType.ROLE,
      then: yup.string().required(),
    }),
  });

  return (
    <Modal
      isOpen={isOpen}
      size="md"
      onRequestClose={handleCancel}
      header={
        k8sRoleTargetAccess === undefined
          ? t('K8S_ROLE_TARGET_ACCESS_CREATE_MODAL_HEADER')
          : t('K8S_ROLE_TARGET_ACCESS_UPDATE_MODAL_HEADER')
      }
    >
      <Formik initialValues={access} validationSchema={validationSchema} onSubmit={handleSubmit}>
        <Form>
          <Stack direction="column" itemSpacing={1}>
            <FormTextField
              data-testid="roleName"
              label={t('K8S_ROLE_TARGET_ACCESS_ROLE_NAME_LABEL')}
              description={t('K8S_ROLE_TARGET_ACCESS_ROLE_NAME_DESCRIPTION')}
              fieldName="roleName"
              placeholder={t('K8S_ROLE_TARGET_ACCESS_ROLE_NAME_PLACEHOLDER')}
              required
            />
            <FormTextField
              data-testid="description"
              label={t('DESCRIPTION')}
              description={t('K8S_ROLE_TARGET_ACCESS_DESCRIPTION_DESCRIPTION')}
              fieldName="description"
              placeholder={t('K8S_ROLE_TARGET_ACCESS_DESCRIPTION_PLACEHOLDER')}
            />
            <FormTextField
              data-testid="adGroups"
              label={t('AD_GROUPS')}
              description={t('AD_GROUPS_DESCRIPTION')}
              fieldName="adGroups"
              placeholder={t('AD_GROUPS_PLACEHOLDER')}
              required
            />
            <FormDropDown
              label={t('K8S_ROLE_TARGET_ACCESS_API_ACCESS_LABEL')}
              description={t('K8S_ROLE_TARGET_ACCESS_API_ACCESS_DESCRIPTION')}
              fieldName="apiAccessType"
              data-testid="foo"
              required
              options={Object.keys(APIAccessType).map((k) => ({
                label: t(apiAccessToLocalizationKey(k as APIAccessType)),
                value: k as APIAccessType,
              }))}
              onChange={(option) => {
                setApiAccessType(option.value as APIAccessType);
              }}
            />
            <FormTextField
              data-testid="namespace"
              label={t('K8S_ROLE_TARGET_ACCESS_NAMESPACE_LABEL')}
              description={t('K8S_ROLE_TARGET_ACCESS_NAMESPACE_DESCRIPTION')}
              fieldName="namespace"
              placeholder={t('K8S_ROLE_TARGET_ACCESS_NAMESPACE_PLACEHOLDER')}
              disabled={apiAccessType === APIAccessType.CLUSTER_ROLE}
            />
            <Stack inline itemSpacing={1}>
              <Button data-testid="submit" type="submit">
                {k8sRoleTargetAccess === undefined ? t('CREATE') : t('UPDATE')}
              </Button>
              <Button type="button" data-testid="cancel" variant="secondary" onClick={handleCancel}>
                {t('CANCEL')}
              </Button>
            </Stack>
          </Stack>
        </Form>
      </Formik>
    </Modal>
  );
};

export default K8sRoleTargetAccessCRUDModal;
